import { useMemo, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Plus, Target, Trash2 } from 'lucide-react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { FormSection } from '@/components/ui/form-section';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import type { ApiError } from '@/core/api';
import { MemberSelect, useMembers } from '@/features/members';
import {
  useCreateKeyResult,
  useCreateObjective,
  useDeleteKeyResult,
  useDeleteObjective,
  useObjectives,
  useUpdateKeyResult,
} from '../queries';
import type { KeyResult, Objective, ObjectiveInput, ObjectiveStatus } from '../types';

const objSchema = z.object({
  title: z.string().trim().min(1, 'Title is required').max(255),
  description: z.string(),
  owner: z.string(),
  period: z.string().max(50),
  status: z.enum(['open', 'achieved', 'missed']),
});
type ObjFormValues = z.infer<typeof objSchema>;

const krSchema = z.object({
  title: z.string().trim().min(1, 'Title is required').max(255),
  target_value: z
    .string()
    .refine((v) => !Number.isNaN(Number(v)) && Number(v) > 0, 'Enter > 0'),
  current_value: z.string().refine((v) => v === '' || !Number.isNaN(Number(v)), 'Enter a number'),
  unit: z.string().max(30),
});
type KrFormValues = z.infer<typeof krSchema>;

const STATUS_LABEL: Record<ObjectiveStatus, string> = {
  open: 'Open',
  achieved: 'Achieved',
  missed: 'Missed',
};
const STATUS_VARIANT: Record<ObjectiveStatus, 'secondary' | 'destructive' | 'outline'> = {
  open: 'outline',
  achieved: 'secondary',
  missed: 'destructive',
};

function ProgressBar({ value }: { value: number }) {
  const v = Math.max(0, Math.min(100, value));
  return (
    <div className="bg-muted h-1.5 w-full overflow-hidden rounded-full">
      <div
        className={cn(
          'h-full transition-all',
          v >= 100 ? 'bg-emerald-600' : v >= 60 ? 'bg-primary' : 'bg-amber-500',
        )}
        style={{ width: `${v}%` }}
      />
    </div>
  );
}

function KeyResultRow({ kr }: { kr: KeyResult }) {
  const remove = useDeleteKeyResult();
  const update = useUpdateKeyResult();
  return (
    <li className="hover:bg-muted/40 group flex items-center gap-3 rounded px-2 py-2">
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <p className="truncate text-sm font-medium">{kr.title}</p>
          <span className="text-muted-foreground shrink-0 text-xs tabular-nums">
            {kr.current_value}/{kr.target_value} {kr.unit}
          </span>
        </div>
        <ProgressBar value={kr.progress_percent} />
      </div>
      <Input
        type="number"
        step="0.01"
        defaultValue={kr.current_value}
        className="h-7 w-20 text-xs"
        aria-label={`Current value for ${kr.title}`}
        onBlur={(e) => {
          const v = e.target.value;
          if (v !== kr.current_value)
            update.mutate({ id: kr.id, input: { current_value: v } });
        }}
      />
      <Button
        size="icon"
        variant="ghost"
        aria-label={`Delete key result ${kr.title}`}
        className="opacity-0 transition-opacity group-hover:opacity-100"
        onClick={() => remove.mutate(kr.id)}
      >
        <Trash2 className="size-3.5" />
      </Button>
    </li>
  );
}

function ObjectiveCard({
  obj,
  ownerLabel,
  onDelete,
}: {
  obj: Objective;
  ownerLabel?: string;
  onDelete: () => void;
}) {
  const [krDialogOpen, setKrDialogOpen] = useState(false);
  const create = useCreateKeyResult();
  const form = useForm<KrFormValues>({
    resolver: zodResolver(krSchema),
    defaultValues: { title: '', target_value: '100', current_value: '0', unit: '' },
  });

  // Aggregate objective-level progress as the average of its KRs.
  const progress = useMemo(() => {
    if (!obj.key_results.length) return 0;
    const sum = obj.key_results.reduce((s, kr) => s + kr.progress_percent, 0);
    return Math.round(sum / obj.key_results.length);
  }, [obj.key_results]);

  async function submitKr(values: KrFormValues) {
    await create.mutateAsync({
      objective: obj.id,
      title: values.title,
      target_value: values.target_value,
      current_value: values.current_value || '0',
      unit: values.unit,
    });
    form.reset({ title: '', target_value: '100', current_value: '0', unit: '' });
    setKrDialogOpen(false);
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-2 space-y-0">
        <div className="min-w-0">
          <CardTitle className="text-base">{obj.title}</CardTitle>
          <CardDescription>
            {ownerLabel ? `${ownerLabel} · ` : ''}
            {obj.period || 'No period'} · {progress}% overall
          </CardDescription>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={STATUS_VARIANT[obj.status]}>{STATUS_LABEL[obj.status]}</Badge>
          <Button size="icon" variant="ghost" aria-label="Delete objective" onClick={onDelete}>
            <Trash2 className="size-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <ProgressBar value={progress} />
        {obj.description ? <p className="text-muted-foreground text-sm">{obj.description}</p> : null}
        <ul className="space-y-1">
          {obj.key_results.length === 0 ? (
            <li className="text-muted-foreground py-2 text-sm">No key results yet.</li>
          ) : (
            obj.key_results.map((kr) => <KeyResultRow key={kr.id} kr={kr} />)
          )}
        </ul>
        <Button size="sm" variant="outline" onClick={() => setKrDialogOpen(true)}>
          <Plus className="size-4" /> Add key result
        </Button>

        <Dialog open={krDialogOpen} onOpenChange={setKrDialogOpen}>
          <DialogContent className="sm:max-w-2xl">
            <DialogHeader>
              <DialogTitle>Add key result</DialogTitle>
            </DialogHeader>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(submitKr)} className="space-y-6">
                <FormSection title="Key result">
                  <FormField
                    control={form.control}
                    name="title"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Title</FormLabel>
                        <FormControl>
                          <Input placeholder="Ship 4 customer demos" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </FormSection>
                <FormSection title="Targets">
                  <div className="grid gap-4 sm:grid-cols-3">
                    <FormField
                      control={form.control}
                      name="target_value"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Target</FormLabel>
                          <FormControl>
                            <Input type="number" min="0" step="0.01" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="current_value"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Current</FormLabel>
                          <FormControl>
                            <Input type="number" min="0" step="0.01" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="unit"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Unit</FormLabel>
                          <FormControl>
                            <Input placeholder="demos" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </FormSection>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setKrDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={create.isPending}>
                    {create.isPending ? 'Saving…' : 'Add'}
                  </Button>
                </DialogFooter>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}

export function KpisGoalsPage() {
  const { data, isPending, isError, error } = useObjectives();
  const { data: membersData } = useMembers();
  const create = useCreateObjective();
  const remove = useDeleteObjective();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [toDelete, setToDelete] = useState<Objective | null>(null);

  const ownerNames = useMemo(() => {
    const map = new Map<number, string>();
    for (const m of membersData?.results ?? []) map.set(m.user.id, m.user.full_name);
    return map;
  }, [membersData]);

  const form = useForm<ObjFormValues>({
    resolver: zodResolver(objSchema),
    defaultValues: { title: '', description: '', owner: '', period: '', status: 'open' },
  });

  async function submit(values: ObjFormValues) {
    const input: ObjectiveInput = {
      title: values.title,
      description: values.description,
      owner: values.owner ? Number(values.owner) : null,
      period: values.period,
      status: values.status,
    };
    try {
      await create.mutateAsync(input);
      setDialogOpen(false);
      form.reset({ title: '', description: '', owner: '', period: '', status: 'open' });
    } catch (e) {
      const err = e as ApiError;
      if (err?.fieldErrors) {
        for (const [field, messages] of Object.entries(err.fieldErrors)) {
          form.setError(field as keyof ObjFormValues, { message: messages.join(' ') });
        }
      }
    }
  }

  const objectives = data?.results ?? [];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">KPIs &amp; Goals</h1>
          <p className="text-muted-foreground text-sm">Objectives and key results across the org.</p>
        </div>
        <Button onClick={() => setDialogOpen(true)}>
          <Plus className="size-4" /> New objective
        </Button>
      </div>

      {isError ? (
        <p className="text-destructive text-sm">
          {(error as unknown as ApiError)?.message ?? 'Failed to load objectives.'}
        </p>
      ) : isPending ? (
        <div className="grid gap-4 sm:grid-cols-2">
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-40 w-full" />
        </div>
      ) : objectives.length ? (
        <div className="grid gap-4 sm:grid-cols-2">
          {objectives.map((o) => (
            <ObjectiveCard
              key={o.id}
              obj={o}
              ownerLabel={o.owner ? ownerNames.get(o.owner) : undefined}
              onDelete={() => setToDelete(o)}
            />
          ))}
        </div>
      ) : (
        <div className="text-muted-foreground flex flex-col items-center gap-2 rounded-md border border-dashed py-12 text-sm">
          <Target className="size-6" />
          No objectives yet. Create one to track progress.
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>New objective</DialogTitle>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(submit)} className="space-y-6">
              <FormSection title="Objective">
                <FormField
                  control={form.control}
                  name="title"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Title</FormLabel>
                      <FormControl>
                        <Input placeholder="Win 5 new clients this quarter" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Description</FormLabel>
                      <FormControl>
                        <Textarea rows={3} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </FormSection>
              <FormSection title="Schedule">
                <div className="grid gap-4 sm:grid-cols-3">
                  <FormField
                    control={form.control}
                    name="owner"
                    render={({ field }) => (
                      <FormItem className="sm:col-span-1">
                        <FormLabel>Owner</FormLabel>
                        <FormControl>
                          <MemberSelect
                            queryKey="okr-owner-picker"
                            value={field.value}
                            onChange={field.onChange}
                            placeholder="Unassigned"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="period"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Period</FormLabel>
                        <FormControl>
                          <Input placeholder="2026 Q3" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="status"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Status</FormLabel>
                        <Select value={field.value} onValueChange={field.onChange}>
                          <FormControl>
                            <SelectTrigger className="w-full">
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="open">Open</SelectItem>
                            <SelectItem value="achieved">Achieved</SelectItem>
                            <SelectItem value="missed">Missed</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </FormSection>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={form.formState.isSubmitting}>
                  {form.formState.isSubmitting ? 'Saving…' : 'Create objective'}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={Boolean(toDelete)} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete objective?</AlertDialogTitle>
            <AlertDialogDescription>
              “{toDelete?.title}” and all its key results will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (toDelete) remove.mutate(toDelete.id);
                setToDelete(null);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
